import { supabase } from '../lib/supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

class PushNotificationService {
  /**
   * Check if push notifications are supported in this browser
   */
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        await this.subscribeUser();
        return true;
      } else if (permission === 'denied') {
        throw new Error('Notification permission denied');
      }
      
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  /**
   * Subscribe user to push notifications
   */
  async subscribeUser() {
    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      // Save subscription to database
      const subscriptionData = subscription.toJSON();
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscription.endpoint,
          keys: subscriptionData.keys,
          user_agent: navigator.userAgent,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,endpoint'
        });

      if (error) throw error;

      console.log('✅ Push subscription saved successfully');
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Remove from database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint);
        }
        
        console.log('✅ Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      throw error;
    }
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus() {
    try {
      if (!this.isSupported()) {
        return { 
          isSubscribed: false, 
          permission: 'unsupported',
          supported: false 
        };
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      return {
        isSubscribed: !!subscription,
        permission: Notification.permission,
        supported: true,
        subscription: subscription ? subscription.toJSON() : null
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { 
        isSubscribed: false, 
        permission: 'default',
        supported: false 
      };
    }
  }

  /**
   * Get user's notification schedules
   */
  async getNotificationSchedules() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('notification_schedules')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(schedule => ({
        id: schedule.id,
        userId: schedule.user_id,
        notificationType: schedule.notification_type,
        scheduleTime: schedule.schedule_time,
        frequency: schedule.frequency,
        enabled: schedule.enabled,
        lastSentAt: schedule.last_sent_at,
        metadata: schedule.metadata,
        createdAt: schedule.created_at,
        updatedAt: schedule.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching notification schedules:', error);
      throw error;
    }
  }

  /**
   * Update or create notification schedule
   */
  async updateNotificationSchedule(notificationType, settings) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('notification_schedules')
        .upsert({
          user_id: user.id,
          notification_type: notificationType,
          schedule_time: settings.scheduleTime || null,
          frequency: settings.frequency || 'daily',
          enabled: settings.enabled !== undefined ? settings.enabled : true,
          metadata: settings.metadata || {},
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,notification_type'
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        notificationType: data.notification_type,
        scheduleTime: data.schedule_time,
        frequency: data.frequency,
        enabled: data.enabled,
        metadata: data.metadata
      };
    } catch (error) {
      console.error('Error updating notification schedule:', error);
      throw error;
    }
  }

  /**
   * Disable all notifications for user
   */
  async disableAllNotifications() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notification_schedules')
        .update({ enabled: false })
        .eq('user_id', user.id);

      if (error) throw error;

      await this.unsubscribe();
      
      console.log('✅ All notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification delivery stats
   */
  async getDeliveryStats(days = 30) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('notification_delivery_log')
        .select('notification_type, status, delivered_at')
        .eq('user_id', user.id)
        .gte('delivered_at', startDate.toISOString())
        .order('delivered_at', { ascending: false });

      if (error) throw error;

      // Calculate stats
      const stats = {
        total: data?.length || 0,
        sent: data?.filter(n => n.status === 'sent').length || 0,
        clicked: data?.filter(n => n.status === 'clicked').length || 0,
        dismissed: data?.filter(n => n.status === 'dismissed').length || 0,
        failed: data?.filter(n => n.status === 'failed').length || 0,
        byType: {}
      };

      // Group by type
      data?.forEach(notification => {
        if (!stats.byType[notification.notification_type]) {
          stats.byType[notification.notification_type] = 0;
        }
        stats.byType[notification.notification_type]++;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching delivery stats:', error);
      throw error;
    }
  }

  /**
   * Helper: Convert VAPID key from base64
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default new PushNotificationService();
